import React, { useState, useRef } from 'react';
import Modal from '@material-ui/core/Modal';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import './UploadDocuments.css';
import { documentSentActivity } from '../../../Helper/userActivityApi';
import ErrorModal from '../../../components/ErrorModal';
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import "pdfjs-dist/build/pdf.worker.entry";

const UploadDocuments = ({ isOpen, onProcess, cancel }) => {
    const [files, setFiles] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
    const fileInputRef = useRef(null);

    const handleProcess = () => {
        const validFiles = files.filter(file => !file.isOversized && !file.isEncrypted);
        const filesData = validFiles.map(file => ({
            file_name: file.name,
            file_type: file.type,
            base64_data: file.data,
            user_id: localStorage.getItem('user_id'),
        }));

        onProcess(filesData);
        documentSentActivity(validFiles.map(file => ({
            "sent_type": "Direct Upload",
            "file_name": file.name,
            "status": "Success",
            "user_id": localStorage.getItem('user_id'),
            "company": localStorage.getItem('companyName'),
            "created_date": new Date()
        })));

        const oversizedFiles = files.filter(file => file.isOversized);
        const encryptedFiles = files.filter(file => file.isEncrypted);

        if (oversizedFiles.length > 0 || encryptedFiles.length > 0) {
            const oversizedMsg = oversizedFiles.length > 0 ? 
                `The following files are more than 4MB:<br/>${oversizedFiles.map((file, index) => `${index + 1}. ${file.name}`).join('<br/>')}` : '';
            
            const encryptedMsg = encryptedFiles.length > 0 ? 
                `The following files are encrypted and cannot be processed:<br/>${encryptedFiles.map((file, index) => `${index + 1}. ${file.name}`).join('<br/>')}` : '';

            setErrorMessage(`${oversizedMsg}${oversizedMsg && encryptedMsg ? '<br/><br/>' : ''}${encryptedMsg}`);
            setIsErrorModalOpen(true);
        } else {
            setFiles([]);
        }
        fileInputRef.current.value = ''; // Reset file input
    };

    const handleCancel = () => {
        setFiles([]);
        cancel();
        fileInputRef.current.value = ''; // Reset file input
    };

    const handleUploadDocuments = (event) => {
        const selectedFiles = event.target.files;
        if (selectedFiles.length > 0) {
            const filesArray = Array.from(selectedFiles);
            Promise.all(filesArray.map(file => readFileAsBase64(file)))
                .then(dataArray => {
                    const updatedFilesPromises = dataArray.map((data, index) => {
                        const file = filesArray[index];
                        return checkIfPdfEncrypted(file).then(isEncrypted => ({
                            name: file.name,
                            type: file.type,
                            data: data, // Base64 data
                            isOversized: file.size > 4 * 1024 * 1024, // Check if file is oversized
                            isEncrypted: isEncrypted
                        }));
                    });

                    Promise.all(updatedFilesPromises)
                        .then(updatedFiles => {
                            setFiles(prevFiles => [...prevFiles, ...updatedFiles]);
                        });
                })
                .catch(error => console.error('Error reading files:', error));
        }
        fileInputRef.current.value = ''; // Reset file input
    };

    const checkIfPdfEncrypted = (file) => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = async function () {
                const typedArray = new Uint8Array(this.result);
                try {
                    await pdfjsLib.getDocument({ data: typedArray }).promise;
                    resolve(false); // PDF not encrypted
                } catch (error) {
                    if (error.name === "PasswordException") {
                        resolve(true); // PDF is encrypted
                    } else {
                        console.error('Error loading PDF:', error);
                        resolve(false); // Handle other errors
                    }
                }
            };
            reader.readAsArrayBuffer(file);
        });
    };
    const readFileAsBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result.split(',')[1]); // Remove "data:application/pdf;base64," header
            reader.onerror = error => reject(error);
            reader.readAsDataURL(file);
        });
    };

    const handleDeleteFile = (index) => {
        setFiles(prevFiles => prevFiles.filter((file, i) => i !== index));
    };

    const handleCloseErrorModal = () => {
        setIsErrorModalOpen(false);
    };

    return (
        <>
            <Modal open={isOpen}>
                <div className='export-modal-box'>
                    <div className='container'>
                        <div className='row'>
                            <div className='col-6'>
                                <h3>Documents</h3>
                            </div>
                            <div className='col-6'>
                                <label htmlFor="file-upload" className="custom-file-upload w-75 fl-r">
                                    <FileUploadIcon /> Upload
                                </label>
                                <input
                                    className='file-selector-button pl-0'
                                    id="file-upload"
                                    type="file"
                                    accept="application/pdf"
                                    multiple
                                    onChange={handleUploadDocuments}
                                    ref={fileInputRef} // Added ref to input element
                                />
                            </div>
                        </div>
                    </div>
                    <div className='container file-container'>
                        <div className='row mh-20'>
                            {files.length > 0 ? (
                                files.map((file, index) => (
                                    <div key={index} className="file-item pt-3">
                                        <span>{index + 1}. {file.name}</span>
                                        {file.isOversized && <span className='file-size-warning'> &gt;4MB</span>}
                                        {file.isEncrypted && <span className='file-encrypted-warning'> (Encrypted)</span>}
                                        <IconButton className='p-0 fl-r' onClick={() => handleDeleteFile(index)}><DeleteIcon /></IconButton>
                                    </div>
                                ))
                            ) : (
                                <span>No documents uploaded</span>
                            )}
                        </div>
                    </div>
                    <div className='col-12'>
                        <p className="warning-message text-center">Please note: You can upload a maximum of 5 files at a time for processing.</p>
                    </div>
                    <Button
                        onClick={handleCancel}
                        className='mt-3 cancel-btn'
                    >
                        <span className='text-trans-none'>Cancel</span>
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        className='fl-r mt-3'
                        style={{ width: '40%' }}
                        onClick={handleProcess}
                        disabled={files.length > 5 || files.length === 0}
                    >
                        <span className='text-trans-none'>Process</span>
                    </Button>
                </div>
            </Modal>
            <ErrorModal
                isOpen={isErrorModalOpen}
                message={errorMessage}
                onClose={handleCloseErrorModal}
            />
        </>
    );
};

export default UploadDocuments;
